import moment from "moment"
import { parseAspectRatio } from "utils/scriptJSON"
import ArtHeroStage from "components/ArtHeroStage"
import PlatformBadgeRow from "components/PlatformBadgeRow"
import TokenFeatures from "components/TokenFeatures"
import TokenProvenance from "components/TokenProvenance"
import Address from "components/Address"
import Loading from "components/Loading"
import MarketplaceTokenDetails from "components/MarketplaceTokenDetails"
import usePageMeta from "hooks/usePageMeta"
import useToken from "hooks/useToken"
import useTokenMetadata from "hooks/useTokenMetadata"
import useTokenProvenance from "hooks/useTokenProvenance"
import { getContractChainId, getContractConfigByAddress } from "utils/contractInfoHelper"
import { collaboratorLabel } from "utils/projectDisplay"
import {
  getArtBlocksImageUrl,
  getArtBlocksTokenUrl,
  getBlockExplorerName,
  getBlockExplorerTokenUrl,
  getOpenSeaAssetUrl,
} from "utils/artBlocksUrls"
import { getLiveGeneratorUrl } from "utils/liveGenerator"
import {
  canRenderTokenLive,
  getNftMetadataAspectRatio,
  getNftMetadataFeatures,
  getNftMetadataGeneratorUrl,
  getNftMetadataImageUrl,
} from "utils/tokenMetadata"
import { findMarketplaceWorkByToken } from "data/marketplaceWorks"
import { getProjectPlatformBadges, getProjectPlatformLinks } from "data/platforms"

interface Props {
  contractAddress: string
  id: string
  chainId?: number
}

const TokenDetails = ({ contractAddress, id, chainId }: Props) => {
  const { loading, error, data } = useToken(`${contractAddress.toLowerCase()}-${id}`)
  const token = data?.token
  const resolvedTokenId = token?.tokenId || id
  const marketplaceWork = findMarketplaceWorkByToken(contractAddress, id)
  const contractConfig = getContractConfigByAddress(contractAddress)
  const resolvedChainId = getContractChainId(contractAddress, chainId)
  const tokenMetadata = useTokenMetadata(contractAddress, resolvedTokenId, resolvedChainId)
  const provenance = useTokenProvenance(contractAddress, resolvedTokenId, resolvedChainId)
  const nftMetadata = tokenMetadata.data
  const hasLiveView = canRenderTokenLive(nftMetadata, resolvedChainId)

  const docTitle = token?.project?.name
    ? `${token.project.name} #${token.invocation} — r4v3n`
    : nftMetadata?.name
    ? `${nftMetadata.name} — r4v3n`
    : undefined
  const ogImage = resolvedChainId
    ? (getNftMetadataImageUrl(nftMetadata) || `https://media-proxy.artblocks.io/${resolvedChainId}/${contractAddress.toLowerCase()}/${resolvedTokenId}.png`)
    : getNftMetadataImageUrl(nftMetadata) || undefined
  usePageMeta(
    docTitle,
    nftMetadata?.description || undefined,
    ogImage,
    `/token/${contractAddress}/${resolvedTokenId}`,
  )

  if (marketplaceWork && (!token || !contractConfig || !resolvedChainId)) {
    return <MarketplaceTokenDetails work={marketplaceWork} />
  }

  if (loading) return <Loading />

  // Subgraph failures should not hide configured Engine tokens: metadata + the
  // live generator are enough to render the page (and MOS needs that path).
  if (!token && contractConfig && resolvedChainId) {
    const fallbackAspectRatio = getNftMetadataAspectRatio(nftMetadata) || 1
    const tokenUrlParams = { chainId: resolvedChainId, contractAddress, tokenId: resolvedTokenId }
    const fallbackTitle = nftMetadata?.name || `Token ${resolvedTokenId}`
    const generatorHref = getLiveGeneratorUrl({
      contractAddress,
      tokenId: resolvedTokenId,
      chainId: resolvedChainId,
      metadataGeneratorUrl: getNftMetadataGeneratorUrl(nftMetadata),
      tokenHash: nftMetadata?.token_hash,
    })
    const imageHref = getNftMetadataImageUrl(nftMetadata) || getArtBlocksImageUrl(tokenUrlParams)
    const metadataHref = getArtBlocksTokenUrl(tokenUrlParams)
    const blockExplorerHref = getBlockExplorerTokenUrl(tokenUrlParams)
    const blockExplorerLabel = getBlockExplorerName(resolvedChainId)
    const openSeaHref = getOpenSeaAssetUrl(tokenUrlParams)
    const ownerAddress = provenance.data?.owner_address
    const features = getNftMetadataFeatures(nftMetadata)

    return (
      <div>
        <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb">
          <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
            Projects
          </a>
          <span className="text-gallery-border">·</span>
          <span className="gallery-label text-gallery-text">{fallbackTitle}</span>
        </nav>

        <div className="gallery-detail-grid">
          <ArtHeroStage
            contractAddress={contractAddress}
            tokenId={resolvedTokenId}
            chainId={resolvedChainId}
            aspectRatio={fallbackAspectRatio}
            title={fallbackTitle}
            metadata={nftMetadata}
            metadataLoading={tokenMetadata.loading}
            hasLiveView={hasLiveView}
            generatorHref={generatorHref}
            imageHref={imageHref}
            caption={
              ownerAddress ? (
                <>
                  Owned by <Address address={ownerAddress} />
                </>
              ) : undefined
            }
          />

          <aside className="gallery-detail-meta mt-12 lg:mt-0">
            <p className="gallery-label text-gallery-muted mb-3">{contractConfig.CHAIN_NAME}</p>
            <h1 className="font-serif text-3xl md:text-4xl leading-tight font-normal mb-6">{fallbackTitle}</h1>

            <dl className="gallery-meta-grid mb-8">
              <div className="gallery-meta-cell col-span-2">
                <dt>Token ID</dt>
                <dd className="font-mono">{resolvedTokenId}</dd>
              </div>
              <div className="gallery-meta-cell">
                <dt>Aspect</dt>
                <dd className="font-mono">{fallbackAspectRatio.toFixed(2)}</dd>
              </div>
              {ownerAddress && (
                <div className="gallery-meta-cell">
                  <dt>Owner</dt>
                  <dd><Address address={ownerAddress} /></dd>
                </div>
              )}
              <div className="gallery-meta-cell col-span-2">
                <dt>Contract</dt>
                <dd className="font-mono break-all">{contractAddress}</dd>
              </div>
            </dl>

            <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <a href={metadataHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">Metadata →</a>
              <a href={blockExplorerHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">{blockExplorerLabel} →</a>
              <a href={openSeaHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">OpenSea →</a>
            </div>

            {nftMetadata?.description && (
              <section className="mb-8">
                <h2 className="gallery-label text-gallery-muted mb-3">About</h2>
                <p className="text-sm leading-relaxed text-gallery-muted">{nftMetadata.description}</p>
              </section>
            )}

            <TokenFeatures features={features} />
            <TokenProvenance
              transfers={provenance.data?.transfers}
              chainId={resolvedChainId}
              loading={provenance.loading}
              error={provenance.error}
            />
          </aside>
        </div>
      </div>
    )
  }

  if (error) return <p className="text-sm text-red-500">Error loading token.</p>
  if (!token || !contractConfig || !resolvedChainId) return null

  const aspectRatio = getNftMetadataAspectRatio(nftMetadata) || token.project.aspectRatio || parseAspectRatio(token.project.scriptJSON) || 1
  const tokenUrlParams = { chainId: resolvedChainId, contractAddress, tokenId: token.tokenId }
  const generatorHref = getLiveGeneratorUrl({
    contractAddress,
    tokenId: token.tokenId,
    chainId: resolvedChainId,
    metadataGeneratorUrl: getNftMetadataGeneratorUrl(nftMetadata),
    tokenHash: nftMetadata?.token_hash,
  })
  const imageHref = getNftMetadataImageUrl(nftMetadata) || getArtBlocksImageUrl(tokenUrlParams)
  const blockExplorerHref = getBlockExplorerTokenUrl(tokenUrlParams)
  const blockExplorerLabel = getBlockExplorerName(resolvedChainId)
  const openSeaHref = getOpenSeaAssetUrl(tokenUrlParams)
  const ownerAddress = provenance.data?.owner_address || token.owner.id
  const features = getNftMetadataFeatures(nftMetadata)
  const collectionHref = `/project/${contractAddress}/${token.project.projectId}`
  const heroTitle = `${token.project.name} #${token.invocation}`
  const platformLinks = getProjectPlatformLinks({
    contractAddress,
    projectId: token.project.projectId,
    tokenId: token.tokenId,
    invocation: token.invocation,
    chainId: resolvedChainId,
  })
  const platformBadges = getProjectPlatformBadges(contractAddress, token.project.projectId, {
    chainId: resolvedChainId,
    tokenId: token.tokenId,
  })

  return (
    <div>
      <nav className="flex items-center gap-2 mb-6" aria-label="Breadcrumb">
        <a href="/projects" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">
          Projects
        </a>
        <span className="text-gallery-border">·</span>
        <a
          href={`/project/${contractAddress}/${token.project.projectId}`}
          className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors"
        >
          {token.project.name}
        </a>
        <span className="text-gallery-border">·</span>
        <span className="gallery-label text-gallery-text">#{token.invocation}</span>
      </nav>

      <div className="gallery-detail-grid">
        <ArtHeroStage
          contractAddress={contractAddress}
          tokenId={token.tokenId}
          chainId={resolvedChainId}
          aspectRatio={aspectRatio}
          title={heroTitle}
          metadata={nftMetadata}
          metadataLoading={tokenMetadata.loading}
          hasLiveView={hasLiveView}
          generatorHref={generatorHref}
          imageHref={imageHref}
          caption={
            <>
              Owned by <Address address={ownerAddress} />
            </>
          }
        />

        <aside className="gallery-detail-meta mt-12 lg:mt-0">
          <p className="gallery-label text-gallery-muted mb-3">
            {moment.unix(token.createdAt).format("LL")}
          </p>
          <h1 className="font-serif text-3xl md:text-4xl leading-tight font-normal mb-2">
            {token.project.name} <span className="text-gallery-muted">#{token.invocation}</span>
          </h1>
          <p className="artist-name text-gallery-muted mb-6">{collaboratorLabel(token.project.artistName)}</p>

          <PlatformBadgeRow badges={platformBadges} className="mb-8" />

          <dl className="gallery-meta-grid mb-8">
            <div className="gallery-meta-cell col-span-2">
              <dt>Token ID</dt>
              <dd className="font-mono">{token.tokenId}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Invocation</dt>
              <dd className="font-mono">#{token.invocation}</dd>
            </div>
            <div className="gallery-meta-cell">
              <dt>Sold</dt>
              <dd>{moment.unix(token.createdAt).format("MMM D, YYYY")}</dd>
            </div>
            <div className="gallery-meta-cell col-span-2">
              <dt>Owner</dt>
              <dd><Address address={ownerAddress} /></dd>
            </div>
          </dl>

          <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-3">
            {platformLinks.map((link) => (
              <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-text hover:text-gallery-accent transition-colors">{link.label} →</a>
            ))}
            <a href={blockExplorerHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">{blockExplorerLabel} →</a>
            <a href={openSeaHref} target="_blank" rel="noopener noreferrer" className="gallery-label text-gallery-muted hover:text-gallery-text transition-colors">OpenSea →</a>
          </div>

          <TokenFeatures features={features} collectionHref={collectionHref} />
          <TokenProvenance
            transfers={provenance.data?.transfers}
            chainId={resolvedChainId}
            loading={provenance.loading}
            error={provenance.error}
          />
        </aside>
      </div>
    </div>
  )
}

export default TokenDetails
